import { useQuery } from '@apollo/client/react';
import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  useTheme,
} from '@mui/material';
import { LogOutIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useLocale } from '@shared/hooks/locale';

const GET_LEAD_REFUSED_REASONS = gql(/* GraphQL */ `
  query GetLeadRefusedReasons {
    dictionaries(where: { type: { _eq: lead_refused_types } }) {
      id
      label
    }
  }
`);

type LeadRefusedReasonsProps = {
  onSelect: (note: string) => void;
};

const LeadRefusedReasons = ({ onSelect }: LeadRefusedReasonsProps) => {
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const { data, loading, error } = useQuery(GET_LEAD_REFUSED_REASONS);

  if (loading) {
    return (
      <List>
        {[...Array(4)].map((_, index) => (
          <ListItem key={index}>
            <ListItemAvatar>
              <Skeleton variant="circular" width={40} height={40} />
            </ListItemAvatar>
            <ListItemText primary={<Skeleton variant="text" width="60%" />} />
          </ListItem>
        ))}
      </List>
    );
  }

  if (error) {
    return <div>Error loading reasons</div>;
  }

  const reasons = data?.dictionaries ?? [];

  return (
    <List>
      {reasons.map(({ id, label }) => (
        <ListItemButton key={id} onClick={() => onSelect(label ?? '')}>
          <ListItemAvatar>
            <Avatar style={{ background: palette.error.main }}>
              <LogOutIcon size={18} />
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary={label} />
        </ListItemButton>
      ))}
    </List>
  );
};

type PassLeadDialogProps = {
  open: boolean;
  onClose: () => void;
  onReasonSelect: (note: string) => void;
  loading?: boolean;
};

export const PassLeadDialog = ({
  open,
  onClose,
  onReasonSelect,
  loading = false,
}: PassLeadDialogProps) => {
  const { t } = useLocale();

  return (
    <SlideDialog
      open={open}
      onClose={onClose}
      title={t('Pass on lead')}
      maxWidth="sm"
      loading={loading}
    >
      <LeadRefusedReasons onSelect={onReasonSelect} />
    </SlideDialog>
  );
};
